import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "./ProtectedLayout";
import HealthBanner from "./HealthBanner";
// Path → browser-tab title. The sidebar already renders the human label
// (Layout.tsx::sections), so this keeps the two in lockstep without
// asking each page component to remember to set its own title.
const TITLES = {
    "/": "Dashboard",
    "/login": "Login",
    "/migration": "Migration",
    "/strategies": "Strategien",
    "/backtesting": "Backtesting",
    "/health": "Health",
    "/users": "Users",
    "/wallets": "Wallets",
    "/settings": "Settings",
    "/lab/instances": "Lab · V2-Native Research",
    "/lab/paper-mirrors": "Lab · V1-Mirrors (Shadow Paper)",
    "/lab/v2-native-paper": "Lab · V2-Native Paper Pipeline",
    "/lab/decisions": "Lab · Decisions",
    "/lab/drift": "Lab · Drift",
    "/lab/hot-scan": "Lab · Hot-Scan",
    "/lab/retraining": "Lab · Retraining",
    "/lab/edge-suggestions": "Lab · Edge-Suggestions",
    "/prod/monitor": "Trade Monitor",
    "/prod/positions": "Positions",
    "/prod/orders": "Orders",
    "/prod/history": "Trade History",
    "/prod/performance": "Performance",
    "/prod/portfolio": "Portfolio",
    "/prod/reconcile": "Reconciliation",
    "/prod/kill-switch": "Kill-Switch",
    "/prod/shadow": "Shadow-Mode",
};
const sections = [
    {
        title: "Dashboard",
        items: [{ to: "/overview", label: "Overview" }],
    },
    {
        title: "Production",
        items: [
            { to: "/prod/positions", label: "Positions" },
            { to: "/prod/monitor", label: "Trade Monitor" },
            { to: "/prod/orders", label: "Orders" },
            { to: "/prod/history", label: "Trade History" },
            { to: "/prod/portfolio", label: "Portfolio" },
            { to: "/prod/performance", label: "Performance" },
            { to: "/prod/kill-switch", label: "Kill-Switch" },
        ],
    },
    {
        title: "System",
        items: [
            { to: "/prod/reconcile", label: "Reconciliation" },
            { to: "/health", label: "Health" },
            { to: "/logs", label: "Logs" },
            { to: "/users", label: "Users", requiresRole: "admin" },
        ],
    },
    {
        title: "Konto",
        items: [
            { to: "/wallets", label: "Wallets / Buckets" },
            { to: "/settings", label: "Settings" },
        ],
    },
    // Research / Advanced — nicht im Alltag genutzt (Operator 15.09.): Lab,
    // Migration (shadow-runner tot, V2 ist Live) & Backtesting hier gebündelt
    // statt als eigene Top-Sektionen. Alle Unterseiten bleiben per URL erreichbar.
    {
        title: "Research / Advanced",
        items: [
            { to: "/strategies", label: "Strategies" },
            { to: "/backtesting", label: "Backtesting" },
            { to: "/lab/instances", label: "Lab" },
            { to: "/migration", label: "V1→V2 Migration" },
        ],
    },
];
function _hasRole(actual, required) {
    const order = { viewer: 0, operator: 1, admin: 2 };
    return (order[actual] ?? 0) >= (order[required] ?? 0);
}
export default function Layout() {
    const location = useLocation();
    const auth = useAuth();
    const isProdRoute = location.pathname.startsWith("/prod");
    useEffect(() => {
        const page = TITLES[location.pathname];
        document.title = page ? `${page} · tb-core` : "tb-core";
    }, [location.pathname]);
    return (_jsxs("div", { style: { display: "flex", minHeight: "100vh" }, children: [_jsxs("aside", { style: {
                    width: 240,
                    flexShrink: 0,
                    background: "#0f172a",
                    color: "white",
                    padding: "1rem",
                    overflowY: "auto",
                }, children: [_jsx("h1", { style: { fontSize: "1.1rem", marginBottom: "1rem" }, children: "tb-core" }), sections.map((section) => (_jsxs("div", { style: { marginBottom: "0.75rem" }, children: [_jsx("h3", { style: {
                                    fontSize: "0.7rem",
                                    color: "#94a3b8",
                                    textTransform: "uppercase",
                                    marginBottom: "0.25rem",
                                }, children: section.title }), section.items
                                .filter((item) => !("requiresRole" in item && item.requiresRole)
                                ? true
                                : _hasRole(auth.role, item.requiresRole))
                                .map((item) => (_jsx(NavLink, { to: item.to, end: item.to === "/", style: ({ isActive }) => ({
                                    display: "block",
                                    padding: "0.4rem 0.5rem",
                                    borderRadius: 4,
                                    color: isActive ? "white" : "#cbd5e1",
                                    background: isActive ? "#1e293b" : "transparent",
                                    marginBottom: 2,
                                    fontSize: "0.9rem",
                                }), children: item.label }, item.to)))] }, section.title)))] }), _jsxs("main", { className: isProdRoute ? "prod-mode" : "lab-mode", style: {
                    flex: 1,
                    minWidth: 0,
                    display: "flex",
                    flexDirection: "column",
                }, children: [_jsx(HealthBanner, {}), _jsx("div", { style: { padding: "1.5rem", flex: 1, overflow: "auto" }, children: _jsx(Outlet, {}) })] })] }));
}
